import React, { useState } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  GripVertical, Trash2, Plus, Settings, ChevronDown, ChevronUp,
  Type, Mail, Phone, Calendar, Hash, FileText, Globe, CheckSquare,
  List, Upload, Eye, EyeOff, Copy, AlertCircle, HelpCircle
} from 'lucide-react';
import { Button } from '../ui/button';

// Field type configurations
export const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type, description: 'Single line text input' },
  { value: 'email', label: 'Email', icon: Mail, description: 'Email address with validation' },
  { value: 'phone', label: 'Phone', icon: Phone, description: 'Phone number input' },
  { value: 'number', label: 'Number', icon: Hash, description: 'Numeric input' },
  { value: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
  { value: 'select', label: 'Dropdown', icon: List, description: 'Single selection from options' },
  { value: 'multiselect', label: 'Multi-Select', icon: CheckSquare, description: 'Multiple selections allowed' },
  { value: 'textarea', label: 'Long Text', icon: FileText, description: 'Multi-line text area' },
  { value: 'country', label: 'Country', icon: Globe, description: 'Country selector' },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare, description: 'Yes/No checkbox' },
  { value: 'file', label: 'File Upload', icon: Upload, description: 'Document or image upload' },
];

// Default field templates
export const DEFAULT_FIELDS = [
  { field_name: 'full_name', field_label: 'Full Name', field_type: 'text', is_required: true, is_default: true },
  { field_name: 'email', field_label: 'Email Address', field_type: 'email', is_required: true, is_default: true },
  { field_name: 'phone', field_label: 'Phone Number', field_type: 'phone', is_required: false, is_default: true },
];

// Preset field templates for quick add
export const PRESET_FIELDS = [
  { field_name: 'organization', field_label: 'Organization/Company', field_type: 'text', is_required: false },
  { field_name: 'job_title', field_label: 'Job Title/Position', field_type: 'text', is_required: false },
  { field_name: 'country', field_label: 'Country', field_type: 'country', is_required: true },
  { field_name: 'dietary_requirements', field_label: 'Dietary Requirements', field_type: 'select', is_required: false, 
    field_options: [
      { value: 'none', label: 'No special requirements' },
      { value: 'vegetarian', label: 'Vegetarian' },
      { value: 'vegan', label: 'Vegan' },
      { value: 'halal', label: 'Halal' },
      { value: 'kosher', label: 'Kosher' },
      { value: 'gluten_free', label: 'Gluten Free' },
      { value: 'other', label: 'Other (please specify)' }
    ]
  },
  { field_name: 'sector', field_label: 'Sector/Industry', field_type: 'select', is_required: false,
    field_options: [
      { value: 'government', label: 'Government' },
      { value: 'ngo', label: 'NGO/Civil Society' },
      { value: 'private', label: 'Private Sector' },
      { value: 'academic', label: 'Academic/Research' },
      { value: 'healthcare', label: 'Healthcare' },
      { value: 'media', label: 'Media' },
      { value: 'sports', label: 'Sports Organization' },
      { value: 'other', label: 'Other' }
    ]
  },
  { field_name: 'special_needs', field_label: 'Special Accessibility Needs', field_type: 'textarea', is_required: false,
    help_text: 'Please describe any accessibility requirements we should be aware of'
  },
  { field_name: 'emergency_contact', field_label: 'Emergency Contact', field_type: 'text', is_required: false },
  { field_name: 'id_document', field_label: 'ID Document Upload', field_type: 'file', is_required: false,
    help_text: 'Upload a copy of your ID (PDF, JPG, PNG - max 5MB)'
  },
];

export const FieldTypeIcon = ({ type, className = "w-4 h-4" }) => {
  const fieldType = FIELD_TYPES.find(ft => ft.value === type);
  if (!fieldType) return <Type className={className} />;
  const IconComponent = fieldType.icon;
  return <IconComponent className={className} />;
};

// Single field editor component
const FieldEditor = ({ field, index, onUpdate, onRemove, allFields }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showConditions, setShowConditions] = useState(false);

  const handleChange = (key, value) => {
    onUpdate(index, key, value);
  };

  const handleOptionChange = (optIndex, key, value) => {
    const newOptions = [...(field.field_options || [])];
    newOptions[optIndex] = { ...newOptions[optIndex], [key]: value };
    handleChange('field_options', newOptions);
  };

  const addOption = () => {
    const newOptions = [...(field.field_options || []), { value: '', label: '' }];
    handleChange('field_options', newOptions);
  };

  const removeOption = (optIndex) => {
    const newOptions = (field.field_options || []).filter((_, i) => i !== optIndex);
    handleChange('field_options', newOptions);
  };

  const addCondition = () => {
    const conditions = field.conditional_rules || { show_when: [] };
    conditions.show_when.push({ field: '', operator: 'equals', value: '' });
    handleChange('conditional_rules', conditions);
  };

  const updateCondition = (condIndex, key, value) => {
    const conditions = { ...field.conditional_rules };
    conditions.show_when[condIndex] = { ...conditions.show_when[condIndex], [key]: value };
    handleChange('conditional_rules', conditions);
  };

  const removeCondition = (condIndex) => {
    const conditions = { ...field.conditional_rules };
    conditions.show_when = conditions.show_when.filter((_, i) => i !== condIndex);
    if (conditions.show_when.length === 0) {
      handleChange('conditional_rules', null);
    } else {
      handleChange('conditional_rules', conditions);
    }
  };

  const needsOptions = ['select', 'multiselect'].includes(field.field_type);
  const hasConditions = field.conditional_rules?.show_when?.length > 0;

  return (
    <motion.div
      layout
      className={`border rounded-lg bg-white overflow-hidden ${field.is_default ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}
      data-testid={`field-editor-${index}`}
    >
      {/* Field Header - Always visible */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 border-b">
        <div className="cursor-grab text-gray-400 hover:text-gray-600">
          <GripVertical className="w-5 h-5" />
        </div>
        
        <FieldTypeIcon type={field.field_type} className="w-5 h-5 text-gray-500" />
        
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={field.field_label}
            onChange={(e) => handleChange('field_label', e.target.value)}
            className="w-full bg-transparent border-0 p-0 font-medium text-gray-900 focus:ring-0 focus:outline-none"
            placeholder="Field Label"
            disabled={field.is_default}
          />
        </div>
        
        <div className="flex items-center gap-2">
          {field.is_required && (
            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded">Required</span>
          )}
          {hasConditions && (
            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded flex items-center gap-1">
              <Eye className="w-3 h-3" /> Conditional
            </span>
          )}
          {field.is_default && (
            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-600 rounded">Default</span>
          )}
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-gray-200 rounded"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {!field.is_default && (
            <button
              onClick={() => onRemove(index)}
              className="p-1 hover:bg-red-100 text-red-500 rounded"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded Editor */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-4">
              {/* Basic Settings Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                  <select
                    value={field.field_type}
                    onChange={(e) => handleChange('field_type', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                    disabled={field.is_default}
                  >
                    {FIELD_TYPES.map(ft => (
                      <option key={ft.value} value={ft.value}>{ft.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field Name (API)</label>
                  <input
                    type="text"
                    value={field.field_name}
                    onChange={(e) => handleChange('field_name', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-green-500"
                    placeholder="field_name"
                    disabled={field.is_default}
                  />
                </div>
              </div>

              {/* Placeholder & Help Text */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
                  <input
                    type="text"
                    value={field.placeholder || ''}
                    onChange={(e) => handleChange('placeholder', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                    placeholder="Enter placeholder text..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Help Text</label>
                  <input
                    type="text"
                    value={field.help_text || ''}
                    onChange={(e) => handleChange('help_text', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                    placeholder="Additional instructions..."
                  />
                </div>
              </div>

              {/* Required Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.is_required}
                    onChange={(e) => handleChange('is_required', e.target.checked)}
                    className="w-4 h-4 text-green-600 rounded"
                    disabled={field.is_default && field.is_required}
                  />
                  <span className="text-sm font-medium text-gray-700">Required field</span>
                </label>
              </div>

              {/* Options Editor for Select/Multiselect */}
              {needsOptions && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">Dropdown Options</label>
                    <Button variant="outline" size="sm" onClick={addOption}>
                      <Plus className="w-3 h-3 mr-1" /> Add Option
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {(field.field_options || []).map((opt, optIdx) => (
                      <div key={optIdx} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt.value}
                          onChange={(e) => handleOptionChange(optIdx, 'value', e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
                          placeholder="value"
                        />
                        <input
                          type="text"
                          value={opt.label}
                          onChange={(e) => handleOptionChange(optIdx, 'label', e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                          placeholder="Display Label"
                        />
                        <button
                          onClick={() => removeOption(optIdx)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    
                    {(!field.field_options || field.field_options.length === 0) && (
                      <p className="text-sm text-gray-500 italic">No options defined. Add at least one option.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Conditional Visibility */}
              <div className="border-t pt-4">
                <button
                  onClick={() => setShowConditions(!showConditions)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-green-600"
                >
                  <Settings className="w-4 h-4" />
                  Conditional Visibility
                  {hasConditions && <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">{field.conditional_rules.show_when.length} rule(s)</span>}
                  {showConditions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {showConditions && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-3">
                      Show this field only when the following conditions are met:
                    </p>
                    
                    {(field.conditional_rules?.show_when || []).map((cond, condIdx) => (
                      <div key={condIdx} className="flex items-center gap-2 mb-2">
                        <select
                          value={cond.field}
                          onChange={(e) => updateCondition(condIdx, 'field', e.target.value)}
                          className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                        >
                          <option value="">Select field...</option>
                          {allFields
                            .filter((f, i) => i < index && f.field_name !== field.field_name)
                            .map(f => (
                              <option key={f.field_name} value={f.field_name}>{f.field_label}</option>
                            ))
                          }
                        </select>
                        
                        <select
                          value={cond.operator}
                          onChange={(e) => updateCondition(condIdx, 'operator', e.target.value)}
                          className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                        >
                          <option value="equals">equals</option>
                          <option value="not_equals">not equals</option>
                          <option value="contains">contains</option>
                          <option value="not_empty">is not empty</option>
                        </select>
                        
                        {cond.operator !== 'not_empty' && (
                          <input
                            type="text"
                            value={cond.value}
                            onChange={(e) => updateCondition(condIdx, 'value', e.target.value)}
                            className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                            placeholder="Value"
                          />
                        )}
                        
                        <button
                          onClick={() => removeCondition(condIdx)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    
                    <Button variant="outline" size="sm" onClick={addCondition} className="mt-2">
                      <Plus className="w-3 h-3 mr-1" /> Add Condition
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main Form Builder Component
const FormFieldBuilder = ({ fields = [], onChange, ticketTypes = [] }) => {
  const [showPresets, setShowPresets] = useState(false);
  
  // Initialize with default fields if empty
  React.useEffect(() => {
    if (fields.length === 0) {
      const initialFields = DEFAULT_FIELDS.map((f, idx) => ({
        ...f,
        id: `default-${idx}`,
        sort_order: idx
      }));
      onChange(initialFields);
    }
  }, []);

  const handleFieldUpdate = (index, key, value) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], [key]: value };
    onChange(updated);
  };

  const handleFieldRemove = (index) => {
    const updated = fields.filter((_, i) => i !== index);
    onChange(updated.map((f, i) => ({ ...f, sort_order: i })));
  };

  const handleReorder = (newOrder) => {
    const updated = newOrder.map((f, i) => ({ ...f, sort_order: i }));
    onChange(updated);
  };

  const addField = (preset = null) => {
    const baseField = preset || {
      field_name: `field_${fields.length + 1}`,
      field_label: 'New Field',
      field_type: 'text',
      is_required: false
    };
    
    const newField = {
      ...baseField,
      id: `new-${Date.now()}`,
      sort_order: fields.length,
      is_new: true
    };
    
    onChange([...fields, newField]);
    setShowPresets(false);
  };

  const duplicateField = (index) => {
    const original = fields[index];
    const duplicate = {
      ...original,
      id: `new-${Date.now()}`,
      field_name: `${original.field_name}_copy`,
      field_label: `${original.field_label} (Copy)`,
      is_default: false,
      sort_order: fields.length
    };
    onChange([...fields, duplicate]);
  };

  return (
    <div className="space-y-4" data-testid="form-field-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Registration Form Fields</h3>
          <p className="text-sm text-gray-500">
            Drag to reorder. Default fields (Name, Email, Phone) are always included.
          </p>
        </div>
        
        <div className="relative">
          <Button
            onClick={() => setShowPresets(!showPresets)}
            variant="outline"
            data-testid="add-field-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Field
          </Button>
          
          {/* Presets Dropdown */}
          <AnimatePresence>
            {showPresets && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
              >
                <div className="p-2 border-b">
                  <p className="text-xs font-medium text-gray-500 uppercase">Quick Add Presets</p>
                </div>
                <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                  {PRESET_FIELDS.map((preset, idx) => (
                    <button
                      key={idx}
                      onClick={() => addField(preset)}
                      className="w-full flex items-center gap-3 p-2 text-left hover:bg-gray-50 rounded-lg"
                    >
                      <FieldTypeIcon type={preset.field_type} className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{preset.field_label}</p>
                        <p className="text-xs text-gray-500">{preset.field_type}</p>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="p-2 border-t">
                  <button
                    onClick={() => addField()}
                    className="w-full flex items-center gap-2 p-2 text-green-600 hover:bg-green-50 rounded-lg"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm font-medium">Create Custom Field</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Fields List with Drag & Drop */}
      <Reorder.Group
        axis="y"
        values={fields}
        onReorder={handleReorder}
        className="space-y-3"
      >
        {fields.map((field, index) => (
          <Reorder.Item
            key={field.id || field.field_name}
            value={field}
            className="cursor-grab active:cursor-grabbing"
          >
            <FieldEditor
              field={field}
              index={index}
              onUpdate={handleFieldUpdate}
              onRemove={handleFieldRemove}
              allFields={fields}
            />
          </Reorder.Item>
        ))}
      </Reorder.Group>

      {/* Empty State */}
      {fields.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 mb-4">No fields configured yet</p>
          <Button onClick={() => addField()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Field
          </Button>
        </div>
      )}

      {/* Helpful Tips */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
        <HelpCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium mb-1">Tips for effective registration forms:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-600">
            <li>Keep required fields minimal to improve completion rates</li>
            <li>Use conditional fields to show relevant questions only when needed</li>
            <li>File uploads are useful for ID verification or certifications</li>
            <li>Help text guides attendees on what information to provide</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FormFieldBuilder;
